import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import ActiveUserChip from "@/components/ActiveUserChip";
import UserPickerDrawer from "@/components/UserPickerDrawer";
import AddCatchDrawer from "@/components/AddCatchDrawer";
import { Home, Fish, Settings as SettingsIcon, Plus } from "lucide-react";

export default function AppShell({ children }) {
  const { activeUser, family } = useApp();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const location = useLocation();

  const isActive = (p) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p);

  return (
    <div className="wc-shell wc-safe-bottom">
      {/* Sticky header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A3D62]/95 text-[#FAF8F3] wc-safe-top"
        data-testid="app-header"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-2xl shrink-0">🎣</span>
            <span className="font-display font-black tracking-tight text-base truncate max-w-[10rem]">
              {family?.comp_name || "Glenlyon"}
            </span>
          </div>
          <ActiveUserChip user={activeUser} onClick={() => setPickerOpen(true)} />
        </div>
      </header>

      {/* Content */}
      <main className="min-h-[70vh]">{children}</main>

      {/* Bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
        data-testid="bottom-nav"
      >
        <div className="max-w-lg mx-auto pointer-events-auto">
          <div className="relative bg-white rounded-t-3xl shadow-[0_-8px_28px_rgba(10,61,98,0.12)] h-20 flex items-center justify-between px-8 border-t border-[#0A3D62]/5 wc-nav-inner">
            <Link
              to="/"
              data-testid="nav-home"
              className={`flex flex-col items-center gap-0.5 py-2 px-4 rounded-xl transition ${
                isActive("/") && location.pathname === "/" ? "text-[#0A3D62]" : "text-[#556B7D]"
              }`}
            >
              <Home
                className="w-6 h-6"
                strokeWidth={location.pathname === "/" ? 3 : 2.2}
              />
              <span className="text-[10px] font-bold uppercase">Board</span>
            </Link>
            <Link
              to="/feed"
              data-testid="nav-feed"
              className={`flex flex-col items-center gap-0.5 py-2 px-4 rounded-xl transition ${
                isActive("/feed") || location.pathname.startsWith("/catch/")
                  ? "text-[#0A3D62]"
                  : "text-[#556B7D]"
              }`}
            >
              <Fish
                className="w-6 h-6"
                strokeWidth={isActive("/feed") || location.pathname.startsWith("/catch/") ? 3 : 2.2}
              />
              <span className="text-[10px] font-bold uppercase">Feed</span>
            </Link>
            {/* Center gold + */}
            <button
              onClick={() => setAddOpen(true)}
              data-testid="add-catch-btn"
              className="absolute left-1/2 -translate-x-1/2 -top-7 w-16 h-16 bg-[#E8A838] rounded-full flex items-center justify-center shadow-lg text-[#1A2A35] active:scale-95 hover:scale-105 transition border-4 border-[#FAF8F3]"
              aria-label="Add catch"
            >
              <Plus className="w-8 h-8" strokeWidth={3.5} />
            </button>
            <div className="w-16" /> {/* spacer for center btn */}
            <Link
              to="/settings"
              data-testid="nav-settings"
              className={`flex flex-col items-center gap-0.5 py-2 px-4 rounded-xl transition ${
                isActive("/settings") ? "text-[#0A3D62]" : "text-[#556B7D]"
              }`}
            >
              <SettingsIcon
                className="w-6 h-6"
                strokeWidth={isActive("/settings") ? 3 : 2.2}
              />
              <span className="text-[10px] font-bold uppercase">Setup</span>
            </Link>
          </div>
        </div>
      </nav>

      <UserPickerDrawer open={pickerOpen} onOpenChange={setPickerOpen} />
      <AddCatchDrawer open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
